/**
 * Cutoff deadline card — the product's signature module. A full-width amber
 * block with the live countdown (honey clock) plus the exact closing datetime.
 * Ticks every second; shifts to an "urgent" accent under one hour.
 *
 * Reused in two places:
 * - Proposal sidebar: full column width, shown only while editable.
 * - Order detail header: same card, with the "Editar pedido" CTA passed as
 * `action` so button and countdown read as one grouped unit.
 *
 * Its only input is a `Date`, which is why it lives here and not under
 * `pages/proposal`: both screens that render it reach it without one page
 * importing from another.
 */
import React, {useEffect, useState} from 'react'
import {formatCountdown, formatCutoffLabel} from '../../lib/dateFormat'
import styles from './cutoffCountdown.module.scss'

export type CutoffCountdownProps = {
  cutoffAt: Date
  /** Optional CTA rendered inside the card, grouped with the countdown. */
  action?: React.ReactNode
  /**
   * 'stacked' (default): action sits below the countdown (narrow rail — proposal).
   * 'row': countdown and action share one line (full-width — order detail header).
   */
  layout?: 'stacked' | 'row'
}

const ONE_HOUR_MS = 3_600_000

export const CutoffCountdown = ({
  cutoffAt,
  action,
  layout = 'stacked',
}: CutoffCountdownProps): React.ReactElement => {
  const [nowMs, setNowMs] = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1_000)
    return () => clearInterval(id)
  }, [])

  const remaining = cutoffAt.getTime() - nowMs
  const isClosed = remaining <= 0
  const isUrgent = remaining > 0 && remaining < ONE_HOUR_MS

  const className = [
    styles['cutoff-countdown'],
    'border-m',
    'full-width',
    layout === 'row' ? styles['cutoff-countdown--row'] : '',
    isUrgent ? styles['cutoff-countdown--urgent'] : '',
    isClosed ? styles['cutoff-countdown--closed'] : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={className} role="timer" aria-live="off">
      <div className={`col-flx flx-gap-4 ${styles['cutoff-countdown__info']}`}>
        <span className={`txt-bold-500 txt-upper ${styles['cutoff-countdown__label']}`}>Cierre del pedido</span>
        <span className={`txt-honey txt-kale ${styles['cutoff-countdown__clock']}`}>{formatCountdown(remaining)}</span>
        <span className={styles['cutoff-countdown__when']}>
          {isClosed ? 'El pedido se ha cerrado' : `Cierra el ${formatCutoffLabel(cutoffAt)}`}
        </span>
      </div>
      {action !== undefined && <div className={`m-t-12 full-width ${styles['cutoff-countdown__action']}`}>{action}</div>}
    </div>
  )
}
